import { useCallback, useEffect, useMemo, useState } from "react";

import { ThreeDots } from "react-loader-spinner";

import { CaseSchema, GetAllCasesApiV1CasesGetData } from "@/api/generated/types.gen";
import { DataTableWithPagination } from "@/components/DataTables/DataTableWithPagination";
import { ActionButton } from "@/components/DataTables/types/data-table";

import { useCases } from "@hooks/queries/use-cases";
import { useIsAdmin, useIsDoctorView } from "@hooks/queries/use-me";

import { UpdateCaseModal } from "../UpdateCaseModal";
import { Button } from "../ui/button";
import { DeleteCaseConfirmation } from "./components/DeleteConfirmation";
import { DeleteCasesConfirmation } from "./components/DeleteMultipleConfirmation";
import { Filter } from "./components/Filter";
import { SpotlightSearch } from "./components/SpotlightSearch";
import {
  CaseListTableColumns,
  CaseTypeColumn,
  DoctorColumn,
} from "./constants/header";

type CasesListProps = {
  onRowClick?: (caseItem: CaseSchema) => void;
};

export const CasesList = ({ onRowClick }: CasesListProps) => {
  const isAdmin = useIsAdmin();
  // Experts see the free-text case type instead of the admin-facing service
  // type slug on the case list (MDRS-325).
  const isDoctorView = useIsDoctorView();
  const [selectedCases, setSelectedCases] = useState<string[]>([]);
  const [filters, setFilters] = useState<GetAllCasesApiV1CasesGetData["query"]>({});
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchValue || undefined }));
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const { data: cases, isLoading } = useCases({
    ...filters,
    status: filters?.status ?? undefined,
    view_type: filters?.view_type ?? undefined,
  });

  const handleFiltersChange = (
    newFilters: GetAllCasesApiV1CasesGetData["query"]
  ) => {
    setFilters((prev) => ({ ...newFilters, search: prev?.search }));
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  // MDRS-230: the Doctor column (name + release state). Admin-only — a doctor
  // must never see the flag governing their own visibility. Read-only here;
  // assigning and releasing happen in the Update case modal.
  //
  // Inserted directly after Examinee Name, matching the design's column order
  // (Claim Number, Examinee, Doctor, Service Type, Appointment, Report Due,
  // Status).
  const columns = useMemo(() => {
    // Experts see the free-text case type instead of the admin-facing
    // service type slug (MDRS-325 split, applied to the list here too).
    const baseColumns = isDoctorView
      ? CaseListTableColumns.map((column) =>
          column.id === "service_type" ? CaseTypeColumn : column
        )
      : CaseListTableColumns;

    if (!isAdmin) return baseColumns;

    const examineeIndex = baseColumns.findIndex(
      (column) => column.id === "examinee_name"
    );
    if (examineeIndex === -1) return [...baseColumns, DoctorColumn];

    return [
      ...baseColumns.slice(0, examineeIndex + 1),
      DoctorColumn,
      ...baseColumns.slice(examineeIndex + 1),
    ];
  }, [isAdmin, isDoctorView]);

  const actions: ActionButton<CaseSchema>[] = [
    {
      component: (caseItem) => <UpdateCaseModal caseItem={caseItem} />,
    },
    {
      component: (caseItem) => <DeleteCaseConfirmation caseItem={caseItem} />,
    },
  ];

  const loadingComponent = (
    <ThreeDots
      visible={true}
      height="60"
      width="60"
      color="#00BFFF"
      radius="9"
      ariaLabel="signing-out-loading"
    />
  );

  return (
    <div className="space-y-3">
      {/* Own card, separate from the Cases table below it (design: Report
          Studio UI Update, Main Pages (Half-Dark)) — not nested inside the
          table card. */}
      {selectedCases.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-primary px-6 py-4">
          <div>
            <p className="font-head text-sm font-semibold text-primary-foreground">
              {selectedCases.length} case{selectedCases.length > 1 ? "s" : ""}{" "}
              selected
            </p>
            <p className="text-xs text-primary-foreground/70">
              Actions apply to the selected cases
            </p>
          </div>

          <div className="flex items-center gap-6">
            <Button
              variant="ghost"
              size="compact"
              className="font-head text-xs font-semibold text-primary-foreground/70 hover:text-primary-foreground hover:underline"
              onClick={() => setSelectedCases([])}
            >
              Clear
            </Button>
            {/* Right-most: the primary action in this bar. */}
            <DeleteCasesConfirmation
              caseIds={selectedCases}
              onCleared={() => setSelectedCases([])}
            />
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <h2 className="text-base font-semibold tracking-[-0.01em]">Cases</h2>

          <div className="flex items-center gap-3">
            <div className="w-[320px] shrink-0">
              <SpotlightSearch
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search claim, examinee or service"
              />
            </div>

            <Filter onFiltersChange={handleFiltersChange} />
          </div>
        </div>

        <DataTableWithPagination
          data={cases || []}
          columns={columns}
          isLoading={isLoading}
          selectable
          selectedItems={selectedCases}
          onSelectionChange={setSelectedCases}
          getItemId={(item) => item.id}
          onRowClick={onRowClick}
          className="rounded-none border-0 border-t border-foreground-muted/20"
          cellClassName="py-3"
          emptyMessage="No cases found"
          loadingComponent={loadingComponent}
          actionsHeaderLabel="Actions"
          actions={actions}
          showActions
          paginationOptions={{
            initialPageSize: 10,
            pageSizeOptions: [10, 20, 50],
          }}
        />
      </div>
    </div>
  );
};
